
import React from "react";
import { TableRow, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, Edit, Save, X, Trash2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserRole } from "@/types/strategy-types";
import { AdminUser } from "@/hooks/admin/users/types";

interface UserTableRowProps {
  user: AdminUser;
  isExpanded: boolean;
  editingUser: string | null;
  editFirstName: string;
  editLastName: string;
  onToggleExpand: (userId: string) => void;
  onEditFirstNameChange: (value: string) => void;
  onEditLastNameChange: (value: string) => void;
  onStartEdit: (user: AdminUser) => void;
  onCancelEdit: () => void;
  onSaveEdit: (userId: string) => void;
  onUpdateRole: (roleId: string, newRole: UserRole) => Promise<void>;
  onUpdateName: (userId: string, firstName: string, lastName: string) => Promise<void>;
  onDeleteUser: (userId: string) => void;
}

const UserTableRow = ({
  user,
  isExpanded,
  editingUser,
  editFirstName,
  editLastName,
  onToggleExpand,
  onEditFirstNameChange,
  onEditLastNameChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onUpdateRole,
  onUpdateName,
  onDeleteUser
}: UserTableRowProps) => {
  const isEditing = editingUser === user.id;

  // Handle role change
  const handleRoleChange = async (newRole: string) => {
    if (!user.roleId) return;
    await onUpdateRole(user.roleId, newRole as UserRole);
  };

  const toggleExpand = () => {
    if (user.role === UserRole.StrategyProvider) {
      onToggleExpand(user.id);
    }
  };
  
  return (
    <TableRow className={isEditing ? "bg-muted/20" : ""}>
      {/* Email */}
      <TableCell>
        {user.email ? user.email : <span className="text-muted-foreground italic">No email</span>}
      </TableCell>
      
      {/* First Name / Last Name */}
      <TableCell>
        {isEditing ? (
          <div className="flex space-x-2">
            <Input 
              value={editFirstName} 
              onChange={(e) => onEditFirstNameChange(e.target.value)}
              placeholder="First Name"
              className="w-24"
            />
            <Input 
              value={editLastName} 
              onChange={(e) => onEditLastNameChange(e.target.value)}
              placeholder="Last Name"
              className="w-24"
            />
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            {user.role === UserRole.StrategyProvider && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="p-0 h-5 w-5" 
                onClick={toggleExpand}
              >
                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </Button>
            )}
            <span>{user.first_name || ''} {user.last_name || ''}</span>
          </div>
        )}
      </TableCell>
      
      {/* Role */}
      <TableCell>
        <Select defaultValue={user.role} onValueChange={handleRoleChange}>
          <SelectTrigger className="w-[140px]">
            <SelectValue>
              <Badge variant={
                user.role === 'admin' ? 'destructive' : 
                user.role === 'strategy_provider' ? 'secondary' : 
                'default'
              }>
                {user.role}
              </Badge>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={UserRole.Admin}>
              <Badge variant="destructive">Admin</Badge>
            </SelectItem>
            <SelectItem value={UserRole.StrategyProvider}>
              <Badge variant="secondary">Strategy Provider</Badge>
            </SelectItem>
            <SelectItem value={UserRole.User}>
              <Badge>User</Badge>
            </SelectItem>
            <SelectItem value={UserRole.Editor}>
              <Badge>Editor</Badge>
            </SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      
      {/* Provider */}
      <TableCell>
        {user.provider_id ? (
          <Badge variant="outline">Has provider</Badge>
        ) : (
          user.role === UserRole.StrategyProvider ? (
            <Badge variant="outline" className="bg-yellow-50 text-yellow-800 border-yellow-300">
              No provider assigned
            </Badge>
          ) : '-'
        )}
      </TableCell>
      
      {/* Created At */}
      <TableCell>
        {new Date(user.created_at).toLocaleDateString()}
      </TableCell>
      
      {/* Actions */}
      <TableCell className="text-right">
        <div className="flex justify-end items-center space-x-1">
          {isEditing ? (
            <>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onSaveEdit(user.id)} 
                className="h-8 w-8 p-0"
              >
                <Save className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onCancelEdit} 
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onStartEdit(user)} 
                className="h-8 w-8 p-0"
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onDeleteUser(user.id)} 
                className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

export default UserTableRow;
